import { useTranslation } from "next-i18next";
import styled from "styled-components";
import { device, size } from "../../../../../constants";
import { useWindowSize } from "../../../../../hooks/useWindowResize";
import { cn } from "../../../../../lib/utils";
import { Button } from "../../../casos/components/Button";
import { Line } from "../../../casos/components/Line";
import { useProducts } from "../../hooks/useProducts";

function Cta() {
  const { t } = useTranslation();
  const { scrollToContactForm } = useProducts();

  return (
    <Button
      className="bg-vivid-03 text-neutral-08 hover:text-neutral-700 tabletL:max-w-[457px]"
      onClick={scrollToContactForm}
    >
      {t("products.squad-ia.hero.ctaButton")}
    </Button>
  );
}

const Section = styled.div`
  width: 100%;
  --height-section-height: 783px;
  height: 100vh;
  min-height: var(--height-section-height);
  max-height: var(--height-section-height);
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: var(--height-section-height);
    background: linear-gradient(
        359.59deg,
        #15191e 7.44%,
        rgba(21, 25, 30, 0.526445) 45.15%,
        rgba(21, 25, 30, 0) 99.54%
      ),
      linear-gradient(178.52deg, #15191e 16.74%, rgba(21, 25, 30, 0) 33.45%);
    z-index: 1;
  }

  @media ${device.laptopM} {
    --height-section-height: 768px;
    &::before {
      background: linear-gradient(
          269.65deg,
          rgba(21, 25, 30, 0) 27.6%,
          rgba(21, 25, 30, 0.605248) 50.48%,
          #15191e 98.95%
        ),
        linear-gradient(0deg, rgba(21, 25, 30, 0.15), rgba(21, 25, 30, 0.15));
    }
  }
`;

export default function HeroSection() {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const isLaptop = width >= size.laptop;

  return (
    <Section>
      <section
        className={cn(
          "relative bg-background-02",
          ["mt-[80px] h-[703px]"],
          ["tabletL:h-[768px] tabletL:mt-0"]
        )}
      >
        <video
          loop
          muted
          autoPlay
          playsInline
          controls={false}
          className={cn(
            "absolute top-0 left-0 z-0 object-cover w-full h-full max-[425px]:object-[-700px_0]",
            "max-tablet:object-[-350px_0]",
            "tabletL:object-[-180px_0]",
            "laptopM:object-[0_-41px]"
          )}
          src={
            isLaptop
              ? "/landings/assets/images/products/squad-ia/squad-ia-video.mp4"
              : "/landings/assets/images/products/squad-ia/squad-ia-video-mobile.mp4"
          }
        />

        <div
          className={cn(
            "h-full",
            "pt-[56px] pl-6 pb-[56px]",
            "tabletL:pt-[160px] tabletL:pl-[86px] tabletL:pb-[86px]",
            "laptopM:pt-[160px] laptopM:pl-[112px] laptopM:pb-[112px]"
          )}
        >
          <div className={cn("z-10 relative h-full")}>
            <Line className="border-vivid-03" />

            <h1
              className={cn(
                "uppercase w-full max-w-[342px]",
                ["text-[26px] leading-[32px] mt-4"],
                ["tablet:mt-6 tablet:text-3xl tablet:leading-[48px]"],
                [
                  "laptopM:mt-6 laptopM:text-[48px] laptopM:leading-[64px] tablet:max-w-max",
                ]
              )}
            >
              <span className="font-bold text-vivid-03">
                {t("products.squad-ia.hero.title.part1")}
              </span>
              <br />
              <span className="inline-block mt-2 font-medium text-white tabletL:mt-0">
                {t("products.squad-ia.hero.title.part2")}
              </span>
            </h1>

            <div
              className={cn(
                "mt-[95px] mb-10 max-w-[316px]",
                "tablet:max-w-[480px]",
                "tabletL:max-w-[660px] tabletL:mt-8"
              )}
            >
              <p
                className={cn(
                  "text-white tracking-[0.9px]",
                  ["text-lg leading-8"],
                  ["tablet:text-xl tablet:leading-9"],
                  ["tabletM:text-[22px] tabletM:leading-[40px]"]
                )}
              >
                {t("products.squad-ia.hero.description.text1")}{" "}
                <span className="font-bold text-white">
                  {t("products.squad-ia.hero.description.highlight1")}
                </span>
                {t("products.squad-ia.hero.description.text2")}{" "}
                <span className="font-bold text-white">
                  {t("products.squad-ia.hero.description.highlight2")}
                </span>{" "}
                {t("products.squad-ia.hero.description.text3")}{" "}
                <span className="font-bold text-white">
                  {t("products.squad-ia.hero.description.highlight3")}{" "}
                </span>
                {t("products.squad-ia.hero.description.text4")}
              </p>
            </div>

            <Cta />
          </div>
        </div>
      </section>
    </Section>
  );
}
